import React from 'react';
import { motion } from 'framer-motion';

const GTMv2Slide02MarketOpportunity: React.FC = () => {
  return (
    <div className="flex flex-col h-full relative overflow-hidden">
      {/* Atmospheric background layers */}
      <div className="absolute inset-0">
        {/* Gradient base */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 120% 80% at 50% 120%, rgba(16, 185, 129, 0.12) 0%, transparent 50%), radial-gradient(ellipse 100% 60% at 80% 20%, rgba(14, 165, 233, 0.08) 0%, transparent 40%)',
          }}
        />
        {/* Horizontal scan lines */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
          }}
        />
        {/* Vignette */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-20 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="text-5xl font-bold text-white tracking-tight mb-3">
            Target Market Segments
          </h2>
          <p className="text-xl text-[#94a3b8]">
            Two distinct buying opportunities from the personalisation gap in Australian education
          </p>
        </motion.div>

        {/* Two segments */}
        <div className="flex-1 grid grid-cols-2 gap-12">
          {/* B2B */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            {/* Glow accent */}
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#10b981] via-[#10b981]/50 to-transparent rounded-full" />
            <div className="absolute -left-4 top-0 w-1 h-24 bg-[#10b981] rounded-full blur-md" />

            <div className="pl-6">
              {/* Header row */}
              <div className="flex items-end justify-between mb-8">
                <div>
                  <div className="text-sm text-[#10b981] tracking-widest uppercase mb-2">Enterprise</div>
                  <h3 className="text-4xl font-bold text-white">B2B</h3>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-light text-white">3,500</div>
                  <div className="text-sm text-[#64748b]">Independent Schools</div>
                </div>
              </div>

              {/* Target description */}
              <div className="mb-8">
                <h4 className="text-xl text-white mb-2">Independent School Leaders</h4>
                <p className="text-[#94a3b8] leading-relaxed">
                  Tier 1 & 2 schools in Sydney, Melbourne, Brisbane. They operate as premium businesses requiring competitive differentiation.
                </p>
              </div>

              {/* Decision makers */}
              <div className="mb-8">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Decision Makers</div>
                <div className="flex gap-3">
                  {['Principal', 'Head of Curriculum', 'IT Director'].map((role) => (
                    <span
                      key={role}
                      className="px-4 py-2 text-sm text-white bg-white/5 border border-white/10 rounded"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>

              {/* Pain points */}
              <div className="mb-8">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Pain Points</div>
                <div className="space-y-2">
                  {[
                    'Teacher burnout from differentiation demands',
                    'Stagnating NAPLAN / HSC / VCE results',
                    'Intense competition for enrolments',
                    'Parents demanding evidence of value',
                  ].map((point) => (
                    <div key={point} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 bg-[#10b981] rounded-full flex-shrink-0" />
                      <span className="text-[#e2e8f0]">{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Value prop */}
              <div className="pt-6 border-t border-white/10">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-2">They Buy</div>
                <div className="text-2xl font-semibold text-[#10b981]">Efficiency & Measurable Results</div>
              </div>
            </div>
          </motion.div>

          {/* B2C */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            {/* Glow accent */}
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0ea5e9] via-[#0ea5e9]/50 to-transparent rounded-full" />
            <div className="absolute -left-4 top-0 w-1 h-24 bg-[#0ea5e9] rounded-full blur-md" />

            <div className="pl-6">
              {/* Header row */}
              <div className="flex items-end justify-between mb-8">
                <div>
                  <div className="text-sm text-[#0ea5e9] tracking-widest uppercase mb-2">Consumer</div>
                  <h3 className="text-4xl font-bold text-white">B2C</h3>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-light text-white">4M</div>
                  <div className="text-sm text-[#64748b]">Australian Students</div>
                </div>
              </div>

              {/* Target description */}
              <div className="mb-8">
                <h4 className="text-xl text-white mb-2">The Aspirational Parent</h4>
                <p className="text-[#94a3b8] leading-relaxed">
                  Parents of Years 3–12 students, typically dual-income households who prioritise education. They are the buyers; students are the users.
                </p>
              </div>

              {/* Persona */}
              <div className="mb-8">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Buyer Persona</div>
                <div
                  className="p-4 rounded border-l-2 border-[#0ea5e9]"
                  style={{ background: 'rgba(14, 165, 233, 0.08)' }}
                >
                  <div className="text-lg text-white font-medium">"The Concerned Orchestrator"</div>
                  <div className="text-sm text-[#94a3b8] mt-1">Seeking academic safety netting without constant supervision</div>
                </div>
              </div>

              {/* Pain points */}
              <div className="mb-8">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Pain Points</div>
                <div className="space-y-2">
                  {[
                    'Anxiety about their child falling behind',
                    'Unable to help with complex homework',
                    'High cost of tutoring ($50–100/hr)',
                    'No visibility into actual learning gaps',
                  ].map((point) => (
                    <div key={point} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 bg-[#0ea5e9] rounded-full flex-shrink-0" />
                      <span className="text-[#e2e8f0]">{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Value prop */}
              <div className="pt-6 border-t border-white/10">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-2">They Buy</div>
                <div className="text-2xl font-semibold text-[#0ea5e9]">Peace of Mind</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GTMv2Slide02MarketOpportunity;
